import { z } from 'zod'

/**
 * Runtime configuration, fetched from `/config.json` at boot.
 *
 * WHY RUNTIME AND NOT BUILD-TIME. `deploy-spa.yml` builds one artifact and then
 * promotes that same commit to production. Baking VITE_* values in would make the
 * artifact environment-specific, so a promotion would have to rebuild — and the
 * bytes reaching production would not be the bytes testing ran. The deploy job
 * writes `config.json` per environment instead, uploaded `no-cache` and
 * invalidated alongside `index.html`.
 *
 * Nothing here is a secret. `domain`, `clientId` and `audience` are public values
 * in every OAuth public-client flow; they end up in the authorize URL regardless.
 * The access token is the credential, and it is held in memory only.
 */

const auth0Schema = z.object({
  mode: z.literal('auth0'),
  domain: z.string().min(1),
  clientId: z.string().min(1),
  audience: z.string().min(1),
})

const runtimeConfigSchema = z.object({
  environment: z.enum(['testing', 'production', 'local']),
  auth: auth0Schema,
  /** Null until the Portal API exists (APP-51). Null means the mock adapter. */
  apiBaseUrl: z.string().url().nullable(),
  /**
   * Temporary switches, each off unless the deploy turns it on.
   *
   * `clientCreate` (APP-71): the "New client" form on the Clients page, for
   * setting up testing. `deploy-spa.yml` sets it for testing only, so a
   * production config never carries it and a promotion cannot switch it on.
   */
  features: z.object({ clientCreate: z.boolean().default(false) }).default({ clientCreate: false }),
})

export type Auth0Config = z.infer<typeof auth0Schema>
export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>

/** The dev-only shape. There is no deployed path to it — see `loadRuntimeConfig`. */
export interface MockConfig {
  environment: 'local'
  auth: { mode: 'mock' }
  apiBaseUrl: null
  features: RuntimeConfig['features']
}

export type AppConfig = RuntimeConfig | MockConfig

export function isMockConfig(config: AppConfig): config is MockConfig {
  return config.auth.mode === 'mock'
}

/** Every temporary feature is on locally. */
const LOCAL_FEATURES: RuntimeConfig['features'] = { clientCreate: true }

const MOCK_CONFIG: MockConfig = {
  environment: 'local',
  auth: { mode: 'mock' },
  apiBaseUrl: null,
  features: LOCAL_FEATURES,
}

/**
 * Dev-only Auth0 override. Set these in `.env.local` to run `npm run dev`
 * against a real tenant; leave them unset and dev runs on the mock adapter.
 *
 * `import.meta.env.DEV` is replaced with the literal `false` in a production
 * build, so rollup drops this branch entirely. That is the guarantee that a
 * deployed bundle cannot reach mock auth no matter what it is served.
 */
function devConfig(): AppConfig {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE

  if (!domain || !clientId || !audience) return MOCK_CONFIG

  return {
    environment: 'local',
    auth: { mode: 'auth0', domain, clientId, audience },
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? null,
    features: LOCAL_FEATURES,
  }
}

export class ConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ConfigError'
  }
}

export async function loadRuntimeConfig(signal?: AbortSignal): Promise<AppConfig> {
  if (import.meta.env.DEV) return devConfig()

  let response: Response
  try {
    // no-store, not no-cache: this file is how a deployed build learns which
    // tenant it belongs to, and a stale copy from a previous environment's
    // visit would point the login at the wrong one.
    response = await fetch('/config.json', { cache: 'no-store', signal })
  } catch (cause) {
    throw new ConfigError(`Could not fetch /config.json: ${String(cause)}`)
  }

  if (!response.ok) {
    throw new ConfigError(`/config.json returned ${response.status}`)
  }

  // The SPA fallback turns any unmatched path into index.html with a 200, so a
  // missing config.json arrives here as HTML and an ok response rather than a
  // 404. Parsing is the only thing that catches it.
  let body: unknown
  try {
    body = await response.json()
  } catch {
    throw new ConfigError(
      '/config.json is not JSON. The deploy did not upload it, and the SPA fallback served index.html instead.',
    )
  }

  const parsed = runtimeConfigSchema.safeParse(body)
  if (!parsed.success) {
    throw new ConfigError(`/config.json is malformed: ${parsed.error.issues.map((i) => `${i.path.join('.')} ${i.message}`).join('; ')}`)
  }

  return parsed.data
}
